import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SUPPORTED_LOCALES, persistLocale, type LocaleCode } from "@/i18n";

type Props = {
  variant?: "ghost" | "outline";
  size?: "sm" | "icon" | "default";
  showLabel?: boolean;
};

export function LanguageSwitcher({
  variant = "ghost",
  size = "sm",
  showLabel = false,
}: Props) {
  const { i18n, t } = useTranslation("common");

  // Avoid SSR/CSR hydration mismatches: the browser language detector may pick
  // a different locale than the SSR default. Render the SSR-stable fallback on
  // first paint, then hydrate to the resolved language on the client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const resolved =
    SUPPORTED_LOCALES.find((l) => l.code === i18n.resolvedLanguage) ??
    SUPPORTED_LOCALES[0];
  const current = mounted ? resolved : SUPPORTED_LOCALES[0];

  function setLocale(code: LocaleCode) {
    void i18n.changeLanguage(code);
    persistLocale(code);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", code);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          aria-label={mounted ? t("language.label") : "Sprache"}
          className="gap-2"
          suppressHydrationWarning
        >
          <Languages className="h-4 w-4" />
          {showLabel ? (
            <span className="text-sm" suppressHydrationWarning>{current.label}</span>
          ) : (
            <span className="text-xs uppercase" suppressHydrationWarning>{current.code}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LOCALES.map((loc) => (
          <DropdownMenuItem
            key={loc.code}
            onClick={() => setLocale(loc.code)}
            className={loc.code === current.code ? "font-semibold" : ""}
          >
            <span className="mr-2">{loc.flag}</span>
            {loc.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

